import React, { CSSProperties, useCallback, useEffect, useMemo, useState } from 'react';
import { PopperPlacementType } from '@material-ui/core/Popper';
import { FilledInputProps } from '@material-ui/core/FilledInput';
import { useSmartFormContext } from '../../../forms/utils/useSmartFormContext';
import { ReadOnly } from '../../ReadOnly/ReadOnly';
import { FieldValue } from '../../../types';
import { TypeaheadOption } from './types';
import { ExtraItemName, getVisibleOptions } from './getVisibleOptions';
import { SelectComponent } from './SelectComponent';
import { useRecentItemsContext } from './RecentItemsProvider';

export interface BaseProps<OptionValue extends any = FieldValue, TargetModel extends any = OptionValue> {
  onMenuClose?(): void;
  /** Called as a callback whenever the value needs to change */
  onValueChange?: (value: OptionValue[] | undefined) => void;
  /** Called whenever we need the formik field set */
  onSetFieldValue(model: TargetModel | undefined | null): void;
  /** Converts the selected options to the model value(s) */
  getValue(option: TypeaheadOption<OptionValue>[] | undefined): {
    targetModel?: TargetModel;
    optionValue?: OptionValue[];
  };
  /** External API event */
  onBlur?(): void;
  /** Called whenever the text changes in the textbox */
  onFilterChange?: (value: string) => void;
  onDropDownClick?: () => void;
  onTextFieldClicked?: () => void;
  error?: string;
  id?: string;
  name: string;
  sort?: boolean;
  multiple?: boolean;
  selected?: TypeaheadOption<OptionValue>[];
  inputStyle?: CSSProperties;
  dropdownIconStyle?: CSSProperties;
  InputProps?: Partial<FilledInputProps>;
  readonlyValueLabel?: string;
  selectedValueLabel?: string;
  className?: string;
  icon?: JSX.Element;
  placement?: PopperPlacementType;
  closeOnSelect?: boolean;
  helpText?: string;
  infoText?: string;
  label: string;
  placeholder?: string;
  disabled?: boolean;
  required?: boolean;
  nullifyNoneOption?: boolean;
  fullWidth?: boolean;
  values?: TypeaheadOption<OptionValue>[];
  excludedValues?: string[];
  allowedValues?: string[];
  filter?: boolean;
  loading?: boolean;
  totalCount?: number;
  dataTest?: string;
  disablePermissionDebugger?: boolean;
  clearable?: boolean;
  /**
   * Called when the user scrolls near the end of the options list. Supplying this opts the dropdown
   * into infinite scrolling.
   */
  onLoadMore?: () => void;
  /** Whether another page of options is available to load. */
  hasNextPage?: boolean;
  /** Whether a `onLoadMore` request is currently in flight. Drives the trailing loading row. */
  loadingMore?: boolean;
}

function sortOptions(options: TypeaheadOption<any>[], sort?: boolean) {
  const sortedOptions = sort
    ? [...options].sort((a, b) =>
        a.label === 'None' ? -1 : b.label === 'None' ? 1 : a.label.trim().localeCompare(b.label.trim())
      )
    : options;

  return sortedOptions;
}

export function SelectTypeaheadBase<OptionValue extends any = FieldValue, TargetModel extends any = OptionValue>(
  props: BaseProps<OptionValue, TargetModel>
) {
  const {
    onSetFieldValue,
    onBlur,
    onMenuClose,
    getValue,
    onValueChange,
    onFilterChange,
    onTextFieldClicked,
    onDropDownClick,
    name,
    error,
    selected,
    excludedValues,
    allowedValues: allowedValuesProp,
    helpText,
    infoText,
    filter = true,
    loading,
    selectedValueLabel,
    readonlyValueLabel,
    totalCount,
    nullifyNoneOption = true,
    fullWidth = true,
    sort = false,
    icon,
    label,
    InputProps,
    className,
    dropdownIconStyle,
    id,
    inputStyle,
    multiple,
    placeholder,
    required,
    placement,
    closeOnSelect,
    dataTest,
    values,
    clearable,
    onLoadMore,
    hasNextPage,
    loadingMore
  } = props;

  const [showAllMenuItems, setShowAllMenuItems] = useState(true);
  const [inputText, setInputText] = useState('');
  const {
    values: formikValues,
    readonly,
    validator,
    contextMap,
    getDisabled,
    translations,
    getFieldPermissions
  } = useSmartFormContext();

  // Infinite scrolling is opt-in: it requires an API-driven loader (`onLoadMore`).
  const infiniteScroll = !!onLoadMore;

  const disabled = props.disabled || getDisabled(name);
  const permissions = getFieldPermissions(name);

  const isApiDriven = !!onFilterChange;
  const showAllValues = showAllMenuItems || !filter || isApiDriven;

  const { recentItems } = useRecentItemsContext();

  // The visible options available to pick
  const visibleOptionsResp = useMemo(
    () =>
      getVisibleOptions(
        translations,
        showAllValues,
        showAllMenuItems,
        isApiDriven,
        filter,
        totalCount,
        inputText,
        loading,
        selectedValueLabel,
        recentItems,
        values,
        excludedValues,
        allowedValuesProp
      ),
    [
      excludedValues,
      translations,
      filter,
      inputText,
      isApiDriven,
      loading,
      values,
      showAllMenuItems,
      showAllValues,
      selectedValueLabel,
      recentItems,
      totalCount,
      allowedValuesProp
    ]
  );

  // Stringify everything so we can do a string check to see if anything
  // has changed.
  const stringifiedElements = JSON.stringify(visibleOptionsResp, (key, val) => {
    if (key === 'icon') return null;
    return val;
  });

  let allowedValues: string[] | undefined;
  if (validator) {
    allowedValues = validator.getAllowedValuesFor(name, formikValues as any, contextMap);
  }

  // Filter the visible options based on the validator rules
  // biome-ignore lint/correctness/useExhaustiveDependencies: exception
  const filteredVisibleElements = useMemo(() => {
    const { visibleElements } = visibleOptionsResp;
    if (allowedValues) {
      const allowedElements = visibleElements.filter((element) =>
        allowedValues?.includes(element.referenceId || element.code || '')
      );

      return sortOptions(allowedElements, sort) as TypeaheadOption<OptionValue>[];
    }

    return sortOptions(visibleElements, sort) as TypeaheadOption<OptionValue>[];
  }, [allowedValues, name, contextMap, formikValues, stringifiedElements, sort]);

  const isFieldNotAllowed = useMemo(() => {
    if (validator) {
      return validator.isFieldDisallowed(name, formikValues as any, contextMap);
    }
    return false;
  }, [validator, name, contextMap, formikValues]);

  // ***************************************
  // If the options change and there is a restriction to the options, nullify the selection
  // ***************************************
  useEffect(() => {
    const firstElementId = visibleOptionsResp?.visibleElements[0]?.id;
    const hasOnlyGotHelperOptions =
      visibleOptionsResp.visibleElements.length === 1 && firstElementId === ExtraItemName.INFO;

    // Dont clear it if there's no options to pick from. A value thats been set can still be rendered
    // by onMissing* functions
    if (loading || visibleOptionsResp.visibleElements.length === 0 || hasOnlyGotHelperOptions) return;

    // If the validator has not returned a list to filter options (allowedValues) then exit early
    if (!allowedValues || allowedValues.length === 0) return;

    const valueInOptions = selected?.every((e) =>
      filteredVisibleElements.find((filtered) => filtered?.value === e.value)
    );

    // The validator has returned a restricted list of options, so if the selected value is not in the list of options, then it must be removed
    if (!valueInOptions && selected && selected.length > 0 && !readonly) {
      onSetFieldValue(nullifyNoneOption ? null : undefined);
    }
  }, [
    loading,
    filteredVisibleElements,
    allowedValues,
    onSetFieldValue,
    nullifyNoneOption,
    selected,
    visibleOptionsResp,
    readonly
  ]);

  // ***************************************
  // Calls the getValue function to convert the options to the model
  // and then notifies formik (onSetFieldValue) as well as triggering some API events (onValueChange)
  // ***************************************
  const setValues = useCallback(
    (selectedValues?: TypeaheadOption<OptionValue>[]) => {
      const { optionValue, targetModel } = getValue(selectedValues);
      onSetFieldValue(targetModel || (nullifyNoneOption ? null : undefined));
      onValueChange?.(optionValue);
    },
    [getValue, nullifyNoneOption, onValueChange, onSetFieldValue]
  );

  // Needs to run after all hooks.
  if (isFieldNotAllowed) {
    return null;
  }

  if (readonly)
    return (
      <ReadOnly
        permissionsId={id}
        icon={icon}
        infoText={infoText}
        label={label}
        value={readonlyValueLabel}
        error={error}
        permission={permissions}
      />
    );

  // Represents the text value in the textfield
  const textValue =
    disabled && multiple
      ? ''
      : (((loading || disabled) && readonlyValueLabel ? readonlyValueLabel : selectedValueLabel) ?? '');

  return (
    <SelectComponent<OptionValue>
      className={className}
      onTextFieldClicked={onTextFieldClicked}
      name={name}
      label={label}
      error={!!error}
      helpText={error || (helpText ?? undefined)}
      infoText={infoText}
      required={required}
      placeholder={placeholder}
      icon={icon}
      loading={loading}
      disabled={disabled}
      closeOnSelect={closeOnSelect}
      placement={placement}
      selected={selected}
      fullWidth={fullWidth}
      itemSize={filteredVisibleElements[0]?.code || filteredVisibleElements[0]?.subtitle ? 'large' : 'small'}
      onMenuClose={onMenuClose}
      onSetInputText={setInputText}
      multiple={multiple}
      onBlur={onBlur}
      InputProps={InputProps}
      onDropDownClick={onDropDownClick}
      filter={filter}
      inputStyle={inputStyle}
      sort={sort}
      onFilterChange={onFilterChange}
      setShowAllMenuItems={setShowAllMenuItems}
      options={filteredVisibleElements}
      setValues={setValues}
      dropdownIconStyle={dropdownIconStyle}
      textValue={textValue}
      dataTest={dataTest}
      clearable={clearable}
      infiniteScroll={infiniteScroll}
      onLoadMore={onLoadMore}
      hasNextPage={hasNextPage}
      loadingMore={loadingMore}
    />
  );
}
